import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ArrowRight, Play, Settings } from 'lucide-react';
import { siteConfig } from '@/config/site';
import { useLanguage } from '@/context/LanguageContext';
import { api } from '@/services/api';
import { IconHouseGuestMale, IconHouseVixenFemale } from '@/components/icons';
import BookSessionCta from '@/components/BookSessionCta';
import AmbientParticles from '@/components/AmbientParticles';

export function HubPage() {
  const { language, toggleLanguage } = useLanguage();
  const navigate = useNavigate();
  const videoRef = useRef(null);
  
  // Animation phases: 'video' -> 'transition' -> 'content'
  const [phase, setPhase] = useState('video');
  const [videoEnded, setVideoEnded] = useState(false);
  const [showPlayButton, setShowPlayButton] = useState(false);
  const [hoveredCard, setHoveredCard] = useState(null);

  useEffect(() => {
    api.trackEvent('page_view', 'hub', '/');
  }, []);

  // Function to handle video end
  const handleVideoEnd = () => {
    setVideoEnded(true);
    setTimeout(() => {
      setPhase('transition');
      setTimeout(() => {
        setPhase('content');
        setVideoEnded(false);
      }, 800);
    }, 200);
  };

  // Handle video playback
  useEffect(() => {
    if (phase === 'video' && videoRef.current) {
      const playVideo = async () => {
        try {
          videoRef.current.currentTime = 0;
          await videoRef.current.play();
        } catch (err) {
          console.log('Autoplay blocked, showing play button');
          setShowPlayButton(true);
        }
      };
      
      // Timeout fallback
      const timeout = setTimeout(() => {
        if (phase === 'video') {
          handleVideoEnd();
        }
      }, 10000);
      
      playVideo();
      
      return () => clearTimeout(timeout);
    }
  }, [phase]);

  const handleManualPlay = () => {
    setShowPlayButton(false);
    if (videoRef.current) {
      videoRef.current.play();
    }
  };

  const handleSkip = () => {
    if (videoRef.current) {
      videoRef.current.pause();
    }
    handleVideoEnd();
  };

  // Logo click navigates to landing (itself, essentially does nothing on hub)
  const handleLogoClick = () => {
    navigate('/');
  };

  const content = {
    en: {
      clientCard: {
        title: 'House Guests',
        description: 'Client-side booking, rates, and visit scheduling.',
        cta: 'Enter',
      },
      privateCard: {
        title: 'Vixen',
        description: 'Exclusive access for referred hosts; orientation + terms + scheduling.',
        cta: 'Enter',
      },
      established: 'Discreet · Premium · Appointment-Based',
    },
    es: {
      clientCard: {
        title: 'Huéspedes',
        description: 'Reservas, tarifas y programación de visitas para clientes.',
        cta: 'Entrar',
      },
      privateCard: {
        title: 'Vixens de Casa',
        description: 'Acceso exclusivo para anfitrionas referidas; orientación + términos + programación.',
        cta: 'Entrar',
      },
      established: 'Discreto · Premium · Solo con Cita',
    },
  };

  const t = content[language];

  // Phase 1: Video Animation
  if (phase === 'video') {
    return (
      <div 
        className={`fixed inset-0 z-50 bg-[#07060B] flex items-center justify-center overflow-hidden transition-opacity duration-800 ${
          videoEnded ? 'opacity-0' : 'opacity-100'
        }`}
        data-testid="hub-animation"
      >
        <video
          ref={videoRef}
          src={siteConfig.animations.hub}
          className="w-full h-full object-contain max-w-5xl"
          playsInline
          muted
          preload="auto"
          onEnded={handleVideoEnd}
          onError={handleVideoEnd}
        />

        {showPlayButton && (
          <button
            onClick={handleManualPlay}
            className="absolute inset-0 flex flex-col items-center justify-center bg-[#07060B]/90 cursor-pointer group"
            data-testid="play-animation-btn"
          >
            <div className="w-24 h-24 rounded-full bg-gradient-to-br from-[#FF4DA6] to-[#8B5CFF] flex items-center justify-center group-hover:scale-110 transition-transform duration-300 shadow-lg shadow-[#FF4DA6]/30">
              <Play className="w-10 h-10 text-white ml-1" />
            </div>
            <p className="mt-4 text-[#A7A3BA] text-sm">Tap to play</p>
          </button>
        )}

        {!showPlayButton && (
          <button
            onClick={handleSkip}
            className="absolute bottom-8 right-8 px-4 py-2 text-sm text-[#A7A3BA] hover:text-[#F4F2FF] transition-colors opacity-0 animate-[fadeIn_0.5s_ease-out_2s_forwards]"
            data-testid="skip-animation-btn"
          >
            Skip →
          </button>
        )}
      </div>
    );
  }

  if (phase === 'transition') {
    return (
      <div className="fixed inset-0 z-50 bg-[#07060B] flex items-center justify-center" />
    );
  }

  // Phase 3: Main Content
  return (
    <>
      <Helmet>
        <title>Casa de Candy</title>
        <meta name="description" content="Casa de Candy - Premium Concierge Experience" />
        <html lang={language} />
      </Helmet>

      <div className="min-h-screen bg-[#07060B] flex flex-col animate-fadeIn" data-testid="hub-page">
        {/* Ambient background glows */}
        <div className="fixed inset-0 pointer-events-none overflow-hidden">
          <div className="absolute top-1/4 left-1/4 w-[500px] h-[500px] rounded-full bg-gradient-radial from-[#FF4DA6]/10 via-transparent to-transparent blur-3xl" />
          <div className="absolute bottom-1/4 right-1/4 w-[500px] h-[500px] rounded-full bg-gradient-radial from-[#27D7FF]/8 via-transparent to-transparent blur-3xl" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full bg-gradient-radial from-[#8B5CFF]/8 via-transparent to-transparent blur-3xl" />
          {/* Softly drifting particles for cinematic depth — restrained opacity */}
          <AmbientParticles count={26} testid="hub-ambient-particles" />
        </div>

        {/* Language Toggle - Top Right */}
        <div className="absolute top-6 right-6 z-20">
          <button
            onClick={toggleLanguage}
            className="px-4 py-2 rounded-full text-sm font-medium tracking-wider border border-white/10 text-[#A7A3BA] hover:text-[#F4F2FF] hover:border-white/20 transition-all backdrop-blur-sm"
            data-testid="hub-language-toggle"
          >
            {language === 'en' ? 'Español' : 'English'}
          </button>
        </div>

        {/* Main Content */}
        <main className="flex-1 flex items-center justify-center px-4 py-12 relative z-10">
          <div className="w-full max-w-4xl">
            {/* Logo - 40% larger, clickable routes to home */}
            <div className="flex justify-center mb-16">
              <button 
                onClick={handleLogoClick}
                className="relative animate-breathe cursor-pointer group"
                title={language === 'en' ? 'Home' : 'Inicio'}
                data-testid="hub-logo-btn"
              >
                <div className="absolute inset-0 -m-20 bg-gradient-radial from-[#8B5CFF]/40 via-[#FF4DA6]/20 to-transparent blur-3xl animate-pulse-glow" />
                <img 
                  src={siteConfig.brand.logoUrl}
                  alt="Casa de Candy"
                  className="w-72 sm:w-80 md:w-96 h-auto object-contain relative z-10 group-hover:scale-105 transition-transform duration-500"
                  style={{
                    filter: 'drop-shadow(0 0 25px rgba(139, 92, 255, 0.8)) drop-shadow(0 0 50px rgba(255, 77, 166, 0.5)) drop-shadow(0 0 75px rgba(39, 215, 255, 0.3))'
                  }}
                  data-testid="hub-logo"
                />
              </button>
            </div>

            {/* Choice Cards with Custom Icons */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
              {/* House Guests (Client) Card - Male Icon */}
              <Link
                to="/client"
                className="group relative block"
                data-testid="hub-client-card"
                onClick={() => api.trackEvent('cta_click', 'hub_client', '/')}
                onMouseEnter={() => setHoveredCard('client')}
                onMouseLeave={() => setHoveredCard(null)}
              >
                <div className="h-full p-8 sm:p-10 rounded-2xl bg-[#111022]/80 border border-[#27D7FF]/20 backdrop-blur-sm transition-all duration-500 group-hover:border-[#27D7FF]/40 group-hover:bg-[#111022] group-hover:shadow-[0_0_60px_rgba(39,215,255,0.15)]">
                  <div className="w-16 h-16 flex items-center justify-center mb-6 group-hover:scale-105 transition-transform duration-500">
                    <IconHouseGuestMale 
                      className="w-full h-full animate-breathe" 
                      isHovered={hoveredCard === 'client'}
                    />
                  </div>
                  <h2 className="font-heading text-2xl font-semibold text-[#F4F2FF] mb-3">
                    {t.clientCard.title}
                  </h2>
                  <p className="text-[#A7A3BA] text-sm mb-8 leading-relaxed">
                    {t.clientCard.description}
                  </p>
                  <div className="flex items-center gap-2 text-[#27D7FF] font-medium text-sm group-hover:gap-3 transition-all duration-300">
                    <span>{t.clientCard.cta}</span>
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-300" />
                  </div>
                </div>
              </Link>

              {/* Vixen Card - Female Icon */}
              <Link
                to="/vixen"
                className="group relative block"
                data-testid="hub-private-card"
                onClick={() => api.trackEvent('cta_click', 'hub_private', '/')}
                onMouseEnter={() => setHoveredCard('private')}
                onMouseLeave={() => setHoveredCard(null)}
              >
                <div className="h-full p-8 sm:p-10 rounded-2xl bg-[#111022]/80 border border-[#FF4DA6]/20 backdrop-blur-sm transition-all duration-500 group-hover:border-[#FF4DA6]/40 group-hover:bg-[#111022] group-hover:shadow-[0_0_60px_rgba(255,77,166,0.15)]">
                  <div className="w-16 h-16 flex items-center justify-center mb-6 group-hover:scale-105 transition-transform duration-500">
                    <IconHouseVixenFemale 
                      className="w-full h-full animate-breathe" 
                      isHovered={hoveredCard === 'private'}
                    />
                  </div>
                  <h2 className="font-heading text-2xl font-semibold text-[#F4F2FF] mb-3">
                    {t.privateCard.title}
                  </h2>
                  <p className="text-[#A7A3BA] text-sm mb-8 leading-relaxed">
                    {t.privateCard.description}
                  </p>
                  <div className="flex items-center gap-2 text-[#FF4DA6] font-medium text-sm group-hover:gap-3 transition-all duration-300">
                    <span>{t.privateCard.cta}</span>
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-300" />
                  </div>
                </div>
              </Link>
            </div>

            {/* Footer */}
            <div className="text-center mt-16">
              <p className="text-[#A7A3BA]/40 text-xs tracking-widest">
                {t.established}
              </p>
              
              {/* Subtle Admin Link */}
              <Link
                to="/admin"
                className="inline-flex items-center gap-1 mt-6 text-[#A7A3BA]/20 hover:text-[#A7A3BA]/40 text-xs transition-colors"
                data-testid="hub-admin-link"
              >
                <Settings className="w-3 h-3" />
                <span>Admin</span>
              </Link>
            </div>
          </div>
        </main>

        {/* Footer credit */}
        <footer className="py-4 text-center">
          <p className="text-[#A7A3BA]/30 text-xs">
            Website by{' '}
            <a 
              href="https://emergent.sh" 
              target="_blank" 
              rel="noopener noreferrer"
              className="hover:text-[#A7A3BA]/50 transition-colors"
            >
              emergent.sh
            </a>
          </p>
        </footer>
      </div>

      {/* Persistent CTA — routes to /client and lands on the booking section */}
      {phase === 'content' && <BookSessionCta routeTo="/client#book" />}
    </>
  );
}

export default HubPage;
 HubPage;
